import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  AdminCategory,
  AdminOrderDetail,
  AdminSettings,
  AdminOrderSummary,
  AdminOverview,
  AdminPayout,
  AdminSettleOrderRequest,
  AdminUserResponse,
  AdminWalletHistory,
  AdminWalletRange,
  AdminWalletSummary,
  AuthResponse,
  Category,
  CategoryOutlinePlan,
  Dispute,
  DisputeEvidence,
  OrderSummary,
  PaginatedResponse,
  PreparePaymentResponse,
  PublicUser,
  RefreshResponse,
  PaymentRecoveryResponse,
  ListingSummary,
  Message,
  Notification,
  OrderActivityScope,
  OrderDocumentKind,
  StorefrontReview,
  StorefrontStatsResponse,
  SubmitEvidenceRequest,
  UserRole,
  UserStatsResponse,
} from '@/types/api'
import { useAuthStore } from '@/stores/auth'

// Endpoints whose own 401s must never trigger another refresh attempt.
const AUTH_PATHS = ['/public/auth/pi', '/auth/refresh', '/auth/logout']
const REFRESH_TOKEN_KEY = 'auth_refresh_token'

// Read from storage at call time so concurrent tabs always share the
// latest session instead of racing stale in-memory copies.
function storedRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

let refreshInFlight: Promise<boolean> | null = null

// Marks request configs that already went through one refresh+retry cycle.
const retriedRequests = new WeakSet<object>()

function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = storedRefreshToken()
    if (!refreshToken) return false
    try {
      const response = await api.refreshSession(refreshToken)
      useAuthStore().setAuth(response.token, response.user)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/** The server closes sockets with this code after a ban or account deletion. */
export const accountClosedCode = 4003

/** Seconds until a JWT's `exp`, or null when the token cannot be read. */
export function secondsUntilExpiry(token: string, now = Date.now()): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    return typeof exp === 'number' ? exp - now / 1000 : null
  } catch {
    return null
  }
}

/**
 * The access token to open a WebSocket with. A socket handshake cannot answer
 * a 401 with a refresh the way REST calls do, and the server closes sockets
 * when their token expires, so renew a token that is about to.
 */
export async function freshAccessToken(marginSeconds = 30): Promise<string | null> {
  const token = useAuthStore().token
  if (!token) return null
  const remaining = secondsUntilExpiry(token)
  // Unreadable: let the server judge it rather than refresh on a guess.
  if (remaining === null || remaining > marginSeconds) return token
  await refreshAccessToken()
  return useAuthStore().token
}

function requireArray<T>(data: unknown, endpoint: string): T[] {
  if (Array.isArray(data)) return data
  throw new Error(`Invalid ${endpoint} response`)
}

class ApiService {
  private client: AxiosInstance
  private baseURL: string

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '/api/v1'
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
    })

    // Request interceptor for auth
    this.client.interceptors.request.use((config) => {
      const authStore = useAuthStore()
      if (authStore.token) {
        config.headers.Authorization = `Bearer ${authStore.token}`
      }
      // Ensure FormData uploads let the browser set the multipart boundary
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers?.delete) {
          config.headers.delete('Content-Type')
        } else if (config.headers) {
          delete config.headers['Content-Type']
        }
      }
      return config
    })

    // Response interceptor: transparently recover expired JWTs via refresh token.
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status !== 401) {
          return Promise.reject(error)
        }

        const config = error.config
        const url = config?.url ?? ''
        const isAuthCall = AUTH_PATHS.some((path) => url.includes(path))

        // One silent refresh + retry per request; concurrent 401s share the
        // same in-flight refresh promise.
        if (!isAuthCall && config && !retriedRequests.has(config) && storedRefreshToken()) {
          retriedRequests.add(config)
          if (await refreshAccessToken()) {
            return this.client.request(config)
          }
        }

        const authStore = useAuthStore()
        await authStore.logout()
        // Navigate in-app so toasts and store state survive the redirect.
        // Imported lazily: the router imports every view, and views import this module.
        if (typeof window !== 'undefined') {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search)
          try {
            const { router } = await import('@/router')
            await router.replace(`/login/?redirect=${redirect}`)
          } catch {
            window.location.href = `/login/?redirect=${redirect}`
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth
  async piAuth(
    accessToken: string,
    refreshToken?: string,
    flow?: string,
    walletAddress?: string,
  ): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/public/auth/pi', {
      accessToken,
      refreshToken,
      flow,
      walletAddress,
    })
    return data
  }

  async refreshSession(refreshToken: string): Promise<RefreshResponse> {
    const { data } = await this.client.post<RefreshResponse>('/public/auth/refresh', {
      refreshToken,
    })
    return data
  }

  async revokeSession(refreshToken: string): Promise<void> {
    await this.client.post('/auth/logout', { refreshToken })
  }

  async getMe() {
    const { data } = await this.client.get('/auth/me')
    return data
  }

  async updateProfile(payload: Partial<{ username: string; email: string; bio: string; avatarURL: string }>) {
    const { data } = await this.client.patch('/auth/me', payload)
    return data
  }

  // Categories
  async getCategories(): Promise<Category[]> {
    const { data } = await this.client.get('/public/categories')
    return requireArray<Category>(data, 'categories')
  }

  async getCategory(id: string) {
    const { data } = await this.client.get(`/public/categories/${id}`)
    return data
  }

  // Uploads
  /**
   * Private uploads (delivery files, chat attachments, dispute evidence) are only
   * readable by the order's participants, through `getPrivateUploadLink`.
   */
  async uploadFiles(
    files: File[],
    options: { visibility?: 'public' | 'private' } = {},
  ): Promise<{ urls: string[] }> {
    if (!files || files.length === 0) {
      return { urls: [] }
    }
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    const { data } = await this.client.post('/uploads', formData, {
      params: options.visibility === 'private' ? { visibility: 'private' } : undefined,
      headers: {
        'Content-Type': undefined,
      },
    })
    return data
  }

  /** A short-lived download URL for a private upload, by file name. */
  async getPrivateUploadLink(name: string): Promise<{ url: string; expiresAt: string }> {
    const { data } = await this.client.get(`/uploads/private/${encodeURIComponent(name)}`)
    return data
  }

  // Listings
  async getListings(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const { data } = await this.client.get('/public/listings', { params })
    return data
  }

  async getListing(id: string) {
    const { data } = await this.client.get(`/public/listings/${id}`)
    return data
  }

  // Public storefronts
  async getStorefrontUser(username: string): Promise<PublicUser> {
    const { data } = await this.client.get(`/public/storefront/${encodeURIComponent(username)}`)
    return data
  }

  async getStorefrontListings(
    username: string,
    params?: Partial<Pick<Record<string, number>, 'page' | 'limit'>>,
  ): Promise<PaginatedResponse<ListingSummary>> {
    const { data } = await this.client.get(`/public/storefront/${encodeURIComponent(username)}/listings`, { params })
    return data
  }

  async getStorefrontReviews(username: string): Promise<StorefrontReview[]> {
    const { data } = await this.client.get(`/public/storefront/${encodeURIComponent(username)}/reviews`)
    return requireArray<StorefrontReview>(data, 'storefront reviews')
  }

  async getStorefrontStats(username: string): Promise<StorefrontStatsResponse> {
    const { data } = await this.client.get(`/public/storefront/${encodeURIComponent(username)}/stats`)
    return data
  }

  async createListing(payload: any) {
    const { data } = await this.client.post('/listings', payload)
    return data
  }

  async updateListing(id: string, payload: any) {
    const { data } = await this.client.patch(`/listings/${id}`, payload)
    return data
  }

  /** The seller's own listing in any state, including paused. */
  async getOwnListing(id: string) {
    const { data } = await this.client.get(`/listings/${id}`)
    return data
  }

  async deleteListing(id: string) {
    await this.client.delete(`/listings/${id}`)
  }

  async pauseListing(id: string) {
    const { data } = await this.client.post(`/listings/${id}/pause`)
    return data
  }

  async reportListing(id: string, reason: string) {
    await this.client.post(`/listings/${id}/report`, { reason })
  }

  // Orders
  async createOrder(payload: { listingID: string }) {
    const { data } = await this.client.post('/orders', payload)
    return data
  }

  async getOrders(role?: string): Promise<OrderSummary[]> {
    const { data } = await this.client.get('/orders', { params: { role } })
    return requireArray<OrderSummary>(data, 'orders')
  }

  async getOrder(id: string) {
    const { data } = await this.client.get(`/orders/${id}`)
    return data
  }

  /** An order PDF, with the file name the server chose. */
  async getOrderDocument(id: string, kind: OrderDocumentKind): Promise<{ blob: Blob; filename: string }> {
    return this.getFile(`/orders/${id}/documents/${kind}`, {}, `talentswap-${kind}-${id.slice(0, 8)}.pdf`)
  }

  /** Bookkeeping CSV of settlement snapshots settled in [from, to] (YYYY-MM-DD, UTC). Admin only. */
  async getSettlementExport(from: string, to: string): Promise<{ blob: Blob; filename: string }> {
    return this.getFile('/admin/settlements.csv', { from, to }, `talentswap-settlements-${from}-to-${to}.csv`)
  }

  /** A binary download; the file name comes from Content-Disposition (exposed via CORS). */
  private async getFile(path: string, params: Record<string, string>, fallbackName: string) {
    const response = await this.client.get<Blob>(path, { params, responseType: 'blob' })
    const disposition = String(response.headers['content-disposition'] ?? '')
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName
    return { blob: response.data, filename }
  }

  async acceptOrder(id: string) {
    const { data } = await this.client.post(`/orders/${id}/accept`)
    return data
  }

  async deliverOrder(id: string, payload: { details: string; attachments?: string[] }) {
    const { data } = await this.client.post(`/orders/${id}/deliver`, payload)
    return data
  }

  async acceptDelivery(id: string) {
    const { data } = await this.client.post(`/orders/${id}/accept-delivery`)
    return data
  }

  async cancelOrder(id: string, reason: string) {
    const { data } = await this.client.post(`/orders/${id}/cancel`, { reason })
    return data
  }

  async createDispute(payload: { orderID: string; reason: string; description: string }): Promise<Dispute> {
    const { data } = await this.client.post<Dispute>('/orders/dispute', payload)
    return data
  }

  // Messages
  async getMessages(orderId: string, page = 1, limit = 50): Promise<PaginatedResponse<Message>> {
    const { data } = await this.client.get(`/orders/${orderId}/messages`, { params: { page, limit } })
    return data
  }

  async sendMessage(orderId: string, payload: { content: string; attachmentURL?: string }) {
    const { data } = await this.client.post(`/orders/${orderId}/messages`, payload)
    return data
  }

  // Disputes
  async getDisputes(): Promise<Dispute[]> {
    const { data } = await this.client.get('/disputes')
    return requireArray<Dispute>(data, 'disputes')
  }

  async getDispute(id: string): Promise<Dispute> {
    const { data } = await this.client.get<Dispute>(`/disputes/${id}`)
    return data
  }

  async submitEvidence(disputeId: string, payload: SubmitEvidenceRequest): Promise<DisputeEvidence> {
    const { data } = await this.client.post<DisputeEvidence>(`/disputes/${disputeId}/evidence`, payload)
    return data
  }

  async appealDispute(disputeId: string, reason: string) {
    const { data } = await this.client.post(`/disputes/${disputeId}/appeal`, { reason })
    return data
  }

  /** Read-only order chat for moderators reviewing a dispute. */
  async getAdminOrderMessages(orderId: string, page = 1, limit = 100): Promise<PaginatedResponse<Message>> {
    const { data } = await this.client.get<PaginatedResponse<Message>>(`/admin/orders/${orderId}/messages`, { params: { page, limit } })
    return data
  }

  // Admin disputes
  async getAdminDisputes(status?: string): Promise<Dispute[]> {
    const { data } = await this.client.get<Dispute[]>('/admin/disputes', { params: { status } })
    return requireArray<Dispute>(data, 'admin disputes')
  }

  async resolveDispute(disputeId: string, payload: { resolution: string; resolutionNotes: string; refundAmountPi?: number }) {
    const { data } = await this.client.post(`/admin/disputes/${disputeId}/resolve`, payload)
    return data
  }

  // Reviews
  async createReview(payload: { orderID: string; rating: number; comment?: string }) {
    const { data } = await this.client.post('/reviews', payload)
    return data
  }

  async getReviews(userId: string) {
    const { data } = await this.client.get(`/users/${userId}/reviews`)
    return data
  }

  async respondToReview(reviewId: string, response: string) {
    const { data } = await this.client.post(`/reviews/${reviewId}/respond`, { response })
    return data
  }

  // Notifications
  async getNotifications(page = 1, limit = 20, unreadOnly = false): Promise<PaginatedResponse<Notification>> {
    const { data } = await this.client.get('/notifications', { params: { page, limit, unread: unreadOnly } })
    return data
  }

  async markOrderNotificationsRead(orderID: string, scope: OrderActivityScope) {
    await this.client.post('/notifications/read-order', { orderID, scope })
  }

  async markNotificationRead(id: string) {
    await this.client.post(`/notifications/${id}/read`)
  }

  async markAllNotificationsRead() {
    await this.client.post('/notifications/read-all')
  }

  async getUnreadCount() {
    const { data } = await this.client.get('/notifications/unread-count')
    return data
  }

  // Payments
  async preparePayment(orderId: string): Promise<PreparePaymentResponse> {
    const { data } = await this.client.post<PreparePaymentResponse>('/payments/prepare', {
      orderID: orderId,
    })
    return data
  }

  async approvePayment(orderId: string, paymentId: string): Promise<void> {
    await this.client.post('/payments/approve', { orderID: orderId, paymentID: paymentId })
  }

  async completePayment(
    orderId: string,
    paymentId: string,
    txid: string,
  ): Promise<PaymentRecoveryResponse> {
    const { data } = await this.client.post<PaymentRecoveryResponse>('/payments/complete', {
      orderID: orderId,
      paymentID: paymentId,
      txid,
    })
    return data
  }

  async recoverPayment(paymentID: string): Promise<PaymentRecoveryResponse> {
    const { data } = await this.client.post<PaymentRecoveryResponse>('/payments/recover', { paymentID })
    return data
  }

  async reconcilePayment(orderID: string): Promise<PaymentRecoveryResponse> {
    const { data } = await this.client.post<PaymentRecoveryResponse>('/payments/reconcile', { orderID })
    return data
  }

  async getEscrowStatus(orderId: string) {
    const { data } = await this.client.get(`/payments/escrow/${orderId}`)
    return data
  }

  // User
  async getUser(id: string): Promise<PublicUser> {
    const { data } = await this.client.get<PublicUser>(`/users/${id}`)
    return data
  }

  async getUserListings(userId: string, params?: Record<string, any>) {
    const { data } = await this.client.get(`/users/${userId}/listings`, { params })
    return data
  }

  async getUserReviews(userId: string) {
    const { data } = await this.client.get(`/users/${userId}/reviews`)
    return data
  }

  async getMyStats(): Promise<UserStatsResponse> {
    const { data } = await this.client.get('/users/me/stats')
    return data
  }

  // Admin
  async getAdminStats() {
    const { data } = await this.client.get('/admin/stats')
    return data
  }

  async getAdminOverview(): Promise<AdminOverview> {
    const { data } = await this.client.get('/admin/overview')
    return data
  }

  async getAdminOrders(params?: Record<string, any>): Promise<PaginatedResponse<AdminOrderSummary>> {
    const { data } = await this.client.get('/admin/orders', { params })
    return data
  }

  async getAdminOrder(id: string): Promise<AdminOrderDetail> {
    const { data } = await this.client.get(`/admin/orders/${id}`)
    return data
  }

  async settleAdminOrder(id: string, payload: AdminSettleOrderRequest): Promise<AdminOrderDetail> {
    const { data } = await this.client.post(`/admin/orders/${id}/settle`, payload)
    return data
  }

  async retryAdminPayout(id: string): Promise<AdminPayout> {
    const { data } = await this.client.post(`/admin/payouts/${id}/retry`)
    return data
  }

  async getAdminPayouts(status?: string): Promise<AdminPayout[]> {
    const { data } = await this.client.get('/admin/payouts', { params: { status } })
    return data
  }

  async reconcileAdminPayouts(): Promise<AdminPayout[]> {
    const { data } = await this.client.post('/admin/payouts/reconcile')
    return data
  }

  async getAdminWalletSummary(): Promise<AdminWalletSummary> {
    const { data } = await this.client.get<AdminWalletSummary>('/admin/wallet/summary')
    return data
  }

  async getAdminWalletHistory(range: AdminWalletRange): Promise<AdminWalletHistory> {
    const { data } = await this.client.get<AdminWalletHistory>('/admin/wallet/history', { params: { range } })
    return data
  }

  async getAdminUsers(params?: Record<string, any>) {
    const { data } = await this.client.get('/admin/users', { params })
    return data
  }

  async banUser(userId: string, reason: string) {
    const { data } = await this.client.post(`/admin/users/${userId}/ban`, { reason })
    return data
  }

  async unbanUser(userId: string) {
    const { data } = await this.client.post(`/admin/users/${userId}/unban`)
    return data
  }

  /** Admin only; the backend refuses moderators and changing your own role. */
  async setUserRole(userId: string, role: UserRole) {
    const { data } = await this.client.patch<AdminUserResponse>(`/admin/users/${userId}/role`, { role })
    return data
  }

  async updateSettings(payload: Partial<AdminSettings>) {
    const { data } = await this.client.patch<AdminSettings>('/admin/settings', payload)
    return data
  }

  async getAdminListings(params?: Record<string, any>) {
    const { data } = await this.client.get('/admin/listings', { params })
    return data
  }

  async approveListing(listingId: string) {
    const { data } = await this.client.post(`/admin/listings/${listingId}/approve`)
    return data
  }

  async rejectListing(listingId: string, reason: string) {
    const { data } = await this.client.post(`/admin/listings/${listingId}/reject`, { reason })
    return data
  }

  // Admin Categories
  async getAdminCategories(): Promise<AdminCategory[]> {
    const { data } = await this.client.get('/admin/categories')
    return requireArray<AdminCategory>(data, 'admin categories')
  }

  async createCategory(name: string, parentID?: string): Promise<AdminCategory> {
    const { data } = await this.client.post('/admin/categories', { name, parentID })
    return data
  }

  async renameCategory(id: string, name: string): Promise<AdminCategory> {
    const { data } = await this.client.patch(`/admin/categories/${id}`, { name })
    return data
  }

  /** `parentID` null moves it to the top level. */
  async moveCategory(id: string, parentID: string | null): Promise<AdminCategory> {
    const { data } = await this.client.post(`/admin/categories/${id}/move`, { parentID })
    return data
  }

  /** `ids` must be the whole sibling group, archived categories included. */
  async reorderCategories(parentID: string | null, ids: string[]): Promise<AdminCategory[]> {
    const { data } = await this.client.post('/admin/categories/reorder', { parentID, ids })
    return data
  }

  async archiveCategory(id: string): Promise<AdminCategory> {
    const { data } = await this.client.post(`/admin/categories/${id}/archive`)
    return data
  }

  async restoreCategory(id: string): Promise<AdminCategory> {
    const { data } = await this.client.post(`/admin/categories/${id}/restore`)
    return data
  }

  async deleteCategory(id: string): Promise<void> {
    await this.client.delete(`/admin/categories/${id}`)
  }

  async getCategoryOutline(): Promise<string> {
    const { data } = await this.client.get<{ text: string }>('/admin/categories/outline')
    return data.text
  }

  /** `apply: false` is a dry run. */
  async planCategoryOutline(payload: { text: string; archiveMissing: boolean; apply: boolean }): Promise<CategoryOutlinePlan> {
    const { data } = await this.client.post<CategoryOutlinePlan>('/admin/categories/outline', payload)
    return data
  }

  // Balance
  async getBalance() {
    const { data } = await this.client.get('/users/me/balance')
    return data
  }

  // Admin Settings
  async getAdminSettings() {
    const { data } = await this.client.get<AdminSettings>('/admin/settings')
    return data
  }

  async deleteAccount() {
    await this.client.delete('/auth/me')
  }
}

export const api = new ApiService()
